import type { GeocodingFeature } from "@mapbox/search-js-core";

import type { Bounds } from "context/PlotMap/geo";

export const SEARCH_RESULT_ZOOM = 10;
/** Typed coordinates are an exact spot, so zoom in to field level. */
export const COORDINATE_RESULT_ZOOM = 17;

export interface SearchResultView {
  /** Present when the result covers an area (town, region) rather than a point. */
  bounds: Bounds | undefined;
  center: [number, number];
  /** Zoom for a point result; falls back to SEARCH_RESULT_ZOOM when omitted. */
  zoom?: number;
}

const isFlatBbox = (bbox: unknown): bbox is [number, number, number, number] =>
  Array.isArray(bbox) &&
  bbox.length === 4 &&
  bbox.every((value) => typeof value === "number");

/** Camera target for a geocoding feature: its bbox if it has one, else its point. */
export const toSearchResultView = (
  feature: GeocodingFeature | undefined,
): SearchResultView | null => {
  const coordinates = feature?.geometry?.coordinates;
  if (coordinates == null || coordinates.length < 2) {
    return null;
  }

  const bbox = feature?.properties?.bbox;
  return {
    bounds: isFlatBbox(bbox)
      ? [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ]
      : undefined,
    center: [coordinates[0], coordinates[1]],
  };
};

const DECIMAL = String.raw`-?\d+(?:\.\d+)?`; // e.g. 41, -93.6
const SEPARATOR = String.raw`\s*[,\s]\s*`; // a comma and/or whitespace

const COORDINATE_PATTERN = new RegExp(
  `^\\s*(${DECIMAL})${SEPARATOR}(${DECIMAL})\\s*$`,
);

/** Parses "lat, lng" text into a point view; null if it isn't a valid coordinate pair. */
export const parseCoordinateQuery = (
  query: string,
): SearchResultView | null => {
  const match = COORDINATE_PATTERN.exec(query);
  if (match == null) {
    return null;
  }

  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }

  return {
    bounds: undefined,
    center: [lng, lat],
    zoom: COORDINATE_RESULT_ZOOM,
  };
};
